import { FC, useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid, Stack } from '@mui/material';
import { omit } from '@npi/buddy-utils';

import { useMutation, useQuery } from '@redwoodjs/web';

import { PrincipalForm, SageConnection } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { PrincipalType } from 'src/components/PrincipalForm/PrincipalForm';
import ReusableGrid from 'src/components/ReusableGrid';
import Status from 'src/components/Status/Status';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { UPDATE_MERCHANT_PRINCIPALS } from 'src/graphql/mutations/addMerchant.mutation';
import { GET_MERCHANT_DASHBOARD } from 'src/graphql/queries/merchants.query';
import { useCreateMerchantSageConnection, useCreateNote } from 'src/hooks';
import useMerchantId from 'src/hooks/useMerchantId';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { PageHeaderVariant } from 'src/layouts/MainLayout/PageHeader';
import buildContact from 'src/utils/buildContact';
import { handlePrincipalSubmit, PrincipalModalTitles } from 'src/utils/ViewMerchant';

import { ContactInformation } from '../../components/SubNavigation/components/Contact';
import SubNavigation, { Step } from '../../components/SubNavigation/SubNavigation';
import { principalInitialValue } from '../AddMerchantPage/steps/MerchantInformation/components/Principal';
import Notes from '../ViewPartnerPage/components/Notes/Notes';
import OutstandingInvoices from '../ViewPartnerPage/components/OutstandingInvoices/OutstandingInvoices';
import PaymentDetails from '../ViewPartnerPage/components/PaymentDetails/PaymentDetails';

import History from './components/History/History';
import Services from './components/Services/Services';

interface ViewMerchantPageProps {
    merchantAccountNumber: string;
}

const ViewMerchantPage: FC<ViewMerchantPageProps> = ({ merchantAccountNumber }) => {
    const [viewPaymentDetails, setViewPaymentDetails] = useState(false);
    const { id } = useMerchantId(merchantAccountNumber);

    // Fetch a user's data by id
    const { loading, data, refetch } = useQuery(GET_MERCHANT_DASHBOARD, {
        variables: { id },
        skip: !id,
        onError: (error) => {
            return showErrorToast(error.message);
        },
    });

    const { onCreateNote } = useCreateNote({
        merchantId: id,
        onRefetch: refetch,
    });

    const { onCreateMerchantSageConnection } = useCreateMerchantSageConnection({
        merchantId: data?.merchantDashboard?.id,
        onRefetch: refetch,
    });

    const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
    const handleAddNoteModal = () => {
        setOpenCreateNoteModal(!openCreateNoteModal);
    };

    const handleCreateNote = (values: { description: string; subject: string }) => {
        onCreateNote({ description: values?.description, subject: values?.subject });
        handleAddNoteModal();
    };

    const [principalModalTitle, setPrincipalModalTitle] = useState('');
    const [principalInitialValues, setPrincipalInitialValues] = useState<PrincipalType>();
    const [openPrincipalModal, setOpenPrincipalModal] = useState(false);
    const [principalIndex, setPrincipalIndex] = useState<number | undefined>(undefined);
    const [newBillingContactMessage, setNewBillingContactMessage] = useState<string>('');

    const handleAddPrincipalInitialValues = () => {
        const personalInfoInitialValues = omit(principalInitialValue, [
            'addresses',
            'isBeneficialOwner',
            'percentageOfOwnership',
            'position',
        ]);
        setPrincipalInitialValues({
            newPrincipalIndex: contactInformation.length + 1,
            principal: personalInfoInitialValues,
        });
    };

    const handleAddPrincipalModal = () => {
        setPrincipalModalTitle(PrincipalModalTitles.ADD);
        setOpenPrincipalModal(true);
        handleAddPrincipalInitialValues();
    };

    const handleCancelPrincipalModal = () => {
        setPrincipalModalTitle('');
        setPrincipalInitialValues(undefined);
        handleAddPrincipalInitialValues();
        setOpenPrincipalModal(false);
        setNewBillingContactMessage('');
    };

    const handleEditPrincipalModal = (index: number) => {
        setPrincipalIndex(index);
        setPrincipalModalTitle(PrincipalModalTitles.EDIT);
        const { principals } = data.merchantDashboard.application.merchantInformation;
        const principalInitialValues = principals[index];

        setPrincipalInitialValues({
            principals,
            newPrincipalIndex: index + 1,
            principal: principalInitialValues,
        });
        setOpenPrincipalModal(true);
    };

    const [updatePrincipals] = useMutation(UPDATE_MERCHANT_PRINCIPALS, {
        onCompleted: ({ updateMerchantPrincipals }) => {
            if (newBillingContactMessage) {
                showSuccessToast(newBillingContactMessage);
            } else {
                showSuccessToast(
                    `Contact details have been created for ${updateMerchantPrincipals.fullName}.`
                );
            }
            refetch();
            handleCancelPrincipalModal();
        },
        onError: () => {
            showErrorToast('Error Updating Contact Details');
        },
    });

    let { merchantDashboard } = data ?? {};

    // formatting of created by values moved to here from the API, otherwise it causes the user relationship to crash
    merchantDashboard = {
        ...merchantDashboard,
        notes: merchantDashboard?.notes?.map((note) => {
            return {
                ...note,
                createdById: note?.createdById,
                createdBy: `${note?.createdBy?.firstName} ${note?.createdBy?.lastName}`,
            };
        }),
    };

    const contactInformation: ContactInformation[] = useMemo(() => {
        const principals = merchantDashboard?.application?.merchantInformation?.principals ?? [];
        return buildContact(principals);
    }, [merchantDashboard]);

    const steps: Step[] = useMemo(() => {
        return contactInformation.map((_contact, index) => {
            return {
                label: `Principal ${index + 1}`,
                step: `principal${index + 1}`,
            };
        });
    }, [contactInformation]);

    const bankDetails = useMemo(() => {
        const bic = merchantDashboard?.bic ?? '';
        const iban = merchantDashboard?.iban ?? '';
        const accountNumber = merchantDashboard?.accountNumber ?? '';
        const sortCode = merchantDashboard?.sortCode ?? '';
        return {
            bic,
            iban,
            accountNumber,
            sortCode,
        };
    }, [merchantDashboard]);

    const merchantLegalName = merchantDashboard?.legalName;
    const merchantTradingName = merchantDashboard?.tradingName;
    const sageContactId = data?.merchantDashboard?.sageContactId;
    const subHeadingDetails = sageContactId
        ? `${merchantAccountNumber} / Sage Contact ID: ${sageContactId}`
        : merchantAccountNumber;

    const merchantLegalNamePlusTradingName = merchantTradingName
        ? `${merchantLegalName} T/A ${merchantTradingName}`
        : `${merchantLegalName} T/A ${merchantLegalName}`;

    if (loading || !data) return <Loader />;

    return (
        <PageContent
            headerVariant={PageHeaderVariant.DASHBOARD}
            heading={merchantLegalNamePlusTradingName}
            subHeading={subHeadingDetails}
            callToActions={
                <Stack
                    width="100%"
                    direction="row"
                    spacing={2}
                    justifyContent={'end'}
                    alignItems={'end'}
                >
                    <SageConnection
                        sageContactId={sageContactId}
                        onRefresh={onCreateMerchantSageConnection}
                    />

                    <Status
                        status={merchantDashboard?.status}
                        id={merchantDashboard?.id}
                        isMerchant
                    />
                </Stack>
            }
        >
            <Grid display="flex" flexDirection="row" gap={2} item>
                <ReusableGrid
                    title="Merchant Contact Information"
                    addRecordButtonConfig={{
                        addRecordButtonText: PrincipalModalTitles.ADD,
                        addRecordCallBack: () => {
                            handleAddPrincipalModal();
                        },
                    }}
                >
                    <SubNavigation
                        steps={steps}
                        contactInformation={contactInformation}
                        handleEdit={(activeStep) => {
                            const index = parseInt(activeStep.split('principal')[1], 10) - 1;
                            handleEditPrincipalModal(index);
                        }}
                    />
                    <ModalBox
                        handleCloseModal={handleCancelPrincipalModal}
                        open={openPrincipalModal}
                        title={principalModalTitle}
                        widthSize="100%"
                    >
                        <PrincipalForm
                            initialValues={principalInitialValues}
                            onCancel={handleCancelPrincipalModal}
                            onSubmit={async (formik) => {
                                handlePrincipalSubmit(formik, {
                                    merchantDashboard,
                                    principalModalTitle,
                                    principalIndex,
                                    setNewBillingContactMessage,
                                    updatePrincipals,
                                });
                            }}
                        />
                    </ModalBox>
                </ReusableGrid>

                <Grid
                    item
                    xs={6}
                    gap={2}
                    container
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    <ReusableGrid xs={12}>
                        <OutstandingInvoices invoices={merchantDashboard.invoices || []} />
                    </ReusableGrid>

                    <ReusableGrid
                        xs={12}
                        title="Payment Details"
                        hasEdit
                        hasView
                        setViewPaymentDetails={setViewPaymentDetails}
                        hasBackGroundImage
                    >
                        <PaymentDetails
                            bank={bankDetails}
                            viewPaymentDetails={viewPaymentDetails}
                        />
                    </ReusableGrid>
                </Grid>
            </Grid>

            <Grid display="flex" flexDirection="row" item>
                <Services services={merchantDashboard?.services} merchantId={id} />
            </Grid>

            <Grid display="flex" flexDirection="row" gap={2} item>
                <ReusableGrid
                    title="Notes"
                    addRecordButtonConfig={{
                        addRecordButtonText: 'Add Note',
                        addRecordCallBack: handleAddNoteModal,
                    }}
                >
                    <Notes notes={merchantDashboard?.notes} refetch={refetch} isMerchant />
                    <Dialog
                        open={openCreateNoteModal}
                        onClose={handleAddNoteModal}
                        aria-labelledby="create-note-offer-dialog"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleAddNoteModal}
                                buttonText="Create Note"
                                header="Create Note"
                                initialValues={{
                                    description: '',
                                    subject: '',
                                }}
                                handleFormSubmit={handleCreateNote}
                                isSubmitModal
                                handleAction={() => {}}
                                message=""
                            />
                        </DialogContent>
                    </Dialog>
                </ReusableGrid>

                <ReusableGrid title="History">
                    <History auditLogs={merchantDashboard?.auditLog || []} />
                </ReusableGrid>
            </Grid>
        </PageContent>
    );
};

export default ViewMerchantPage;
